export type RealtimeClientAction =
  | { action: 'ping' }
  | { action: 'page/view'; pageId: string }
  | { action: 'page/unview'; pageId: string }
  | { action: 'page/broadcast'; pageId: string; data: Record<string, unknown> }
  | { action: 'shift/view'; shiftId: string }
  | { action: 'shift/unview'; shiftId: string }
  | {
      action: 'shift/broadcast';
      shiftId: string;
      data: Record<string, unknown>;
    };

export type RealtimeServerEvent =
  | { eventType: 'page/viewers'; pageId: string; userIds: string[] }
  | {
      eventType: 'page/broadcast';
      pageId: string;
      data: Record<string, unknown>;
    }
  | { eventType: 'shift/viewers'; shiftId: string; userIds: string[] }
  | {
      eventType: 'shift/broadcast';
      shiftId: string;
      data: Record<string, unknown>;
    };

export const PORTAL_REALTIME_PAGE_ID = 'portal';

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function isStringList(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(isNonEmptyString);
}

export function parseRealtimeServerEvent(
  value: unknown,
): RealtimeServerEvent | null {
  if (!isRecord(value)) {
    return null;
  }

  switch (value['eventType']) {
    case 'page/viewers':
      return isNonEmptyString(value['pageId']) && isStringList(value['userIds'])
        ? {
            eventType: 'page/viewers',
            pageId: value['pageId'],
            userIds: value['userIds'],
          }
        : null;
    case 'shift/viewers':
      return isNonEmptyString(value['shiftId']) &&
        isStringList(value['userIds'])
        ? {
            eventType: 'shift/viewers',
            shiftId: value['shiftId'],
            userIds: value['userIds'],
          }
        : null;
    case 'page/broadcast':
      return isNonEmptyString(value['pageId']) && isRecord(value['data'])
        ? {
            eventType: 'page/broadcast',
            pageId: value['pageId'],
            data: value['data'],
          }
        : null;
    case 'shift/broadcast':
      return isNonEmptyString(value['shiftId']) && isRecord(value['data'])
        ? {
            eventType: 'shift/broadcast',
            shiftId: value['shiftId'],
            data: value['data'],
          }
        : null;
    default:
      return null;
  }
}

export function isRealtimeServerEvent(
  value: unknown,
): value is RealtimeServerEvent {
  return parseRealtimeServerEvent(value) != null;
}
